import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { useIntl } from "react-intl";
import { VIEW_PERMISSION_MAP } from "@/util/permissionUtil";
import { useScenario } from "@/query/scenario";
import { useProfile } from "@/query/profile";
import { useReactGA, useScenarioId, useRouteHandler, useWebsocket } from "@/hook";
import { Error404View } from "@/component/error/ErrorView";
import ScenarioContextSideMenu from "@/component/scenarioContext/ScenarioContextSideMenu";
import ScenarioContentLayout from "@/component/scenarioContext/ScenarioContentLayout";

import BulkTestView from "./test/BulkTestView";
import DashboardView from "./dashboard/DashboardView";
import DeploymentView from "./deployment/DeploymentView";
import MemberView from "./member/MemberView";
import SettingView from "./setting/SettingView";
import ScenarioEditView from "./scenarioEdit/ScenarioEditView";
import TemplateEditView from "./templateEdit/TemplateEditView";
import ConversationView from "./conversationHistory/ConversationHistoryView";
import IntegrationView from "./integration/IntegrationView";
import WebchatIntegrationView from "./integration/webchat/WebchatIntegrationView";
import KakaotalkIntegrationView from "./integration/kakaotalk/KakaotalkIntegrationView";


const ScenarioContextView: React.FC = () => {
    const intl = useIntl();
    const scenarioId = useScenarioId();
    const location = useLocation();
    const { isPending } = useScenario(scenarioId);
    const { removeStateKey } = useRouteHandler();
    const { connect, clear } = useWebsocket();

    const [resetKey, setResetKey] = useState<number>(0);
    
    const { data: user } = useProfile();

    useEffect(() => {
        if (location.state?.from === "sidemenu") {
            setResetKey(prev => prev + 1);
            removeStateKey('from');
        }
    }, [location, setResetKey, removeStateKey]);

    useEffect(() => {
        if (user) {
            connect(scenarioId, user);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scenarioId, user]);

    useEffect(() => {
        return () => {
            clear();
        };
    }, [clear]);

    useReactGA();

    if (isPending) {
        return null;
    }
    const IntegrationLayout = ({ children }: { children: React.ReactNode }) => (
        <ScenarioContentLayout label={intl.formatMessage({ id: "m07" })}
                        permissions={VIEW_PERMISSION_MAP.INTEGRATION}
        >
            {children}
        </ScenarioContentLayout>
    );

    return (
        <div className="w-screen h-screen flex bg-black-100">
            <ScenarioContextSideMenu />
            <Routes key={resetKey}>
                <Route index element={<Navigate replace to="dashboard"/>}/>
                <Route path="dashboard" element={
                    <ScenarioContentLayout label={intl.formatMessage({ id: "m01" })}>
                        <DashboardView/>
                    </ScenarioContentLayout>
                }/>
                <Route path="scenario-edit" element={
                    <ScenarioContentLayout
                        label={intl.formatMessage({ id: "m02" })}
                        permissions={VIEW_PERMISSION_MAP.SCENE}
                    >
                        <ScenarioEditView/>
                    </ScenarioContentLayout>
                }/>
                <Route path="template" element={
                    <ScenarioContentLayout
                        label={intl.formatMessage({ id: "m03" })}
                        permissions={VIEW_PERMISSION_MAP.SCENE_TEMPLATE}
                    >
                        <TemplateEditView/>
                    </ScenarioContentLayout>
                }/>
                <Route path="test" element={
                    <ScenarioContentLayout
                        label={intl.formatMessage({ id: "m04" })}
                        permissions={VIEW_PERMISSION_MAP.BULK_TEST}
                    >
                        <BulkTestView/>
                    </ScenarioContentLayout>
                }/>
                <Route path="deploy" element={
                    <ScenarioContentLayout
                        label={intl.formatMessage({ id: "m05" })}
                        permissions={VIEW_PERMISSION_MAP.DEPLOYMENT}
                    >
                        <DeploymentView/>
                    </ScenarioContentLayout>
                }/>
                <Route path="conversation" element={
                    <ScenarioContentLayout
                        label={intl.formatMessage({ id: "m06" })}
                        permissions={VIEW_PERMISSION_MAP.CONVERSATION_HISTORY}
                    >
                        <ConversationView/>
                    </ScenarioContentLayout>
                }/>
                <Route path="integration">
                    <Route index element={
                        <IntegrationLayout>
                            <IntegrationView/>
                        </IntegrationLayout>
                    }/>
                    <Route path="webchat" element={
                        <IntegrationLayout>
                            <WebchatIntegrationView/>
                        </IntegrationLayout>
                    }/>
                    <Route path="kakaotalk" element={
                        <IntegrationLayout>
                            <KakaotalkIntegrationView/>
                        </IntegrationLayout>
                    }/>
                </Route>
                <Route path="setting" element={
                    <ScenarioContentLayout
                        label={intl.formatMessage({ id: "m08" })}
                        permissions={VIEW_PERMISSION_MAP.SETTING}
                    >
                        <SettingView/>
                    </ScenarioContentLayout>
                }/>
                <Route path="member" element={
                    <ScenarioContentLayout
                        label={intl.formatMessage({ id: "m09" })}
                        permissions={VIEW_PERMISSION_MAP.MEMBER}
                    >
                        <MemberView/>
                    </ScenarioContentLayout>
                }/>
                <Route path="*" element={
                    <Error404View />
                }/>
            </Routes>
        </div>
    );
};

export default ScenarioContextView;
